import { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Loader2, Layers, TrendingUp } from "lucide-react";
import { formatCurrency, formatFullCurrency } from "./projectUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Comparativa de costes por línea de servicio.
 * Fuentes: pg07_projects (business_areas) + pilots (business_line).
 */
export function CostComparisonCard({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const year = new Date().getFullYear();

  useEffect(() => {
    if (!token) return;
    let c = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/cost-comparison/by-business-line?year=${year}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) {
          const j = await r.json();
          if (!c) setData(j);
        }
      } catch { /* silent */ }
      finally { if (!c) setLoading(false); }
    })();
    return () => { c = true; };
  }, [token, year]);

  if (loading) return <Card><CardContent className="p-8 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-emerald-600" /></CardContent></Card>;
  if (!data || !data.rows?.length) return null;

  const maxPlanned = Math.max(...data.rows.map(r => r.total_planned));

  return (
    <Card data-testid="cost-comparison-card">
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-1">Comparativa costes por línea</div>
        <h3 className="text-lg font-bold flex items-center gap-2 mb-3">
          <Layers className="w-4 h-4" /> ¿Dónde invertimos y dónde escalamos? · {data.year}
        </h3>
        <div className="text-xs text-muted-foreground mb-4">
          {data.totals.lines} líneas · {data.totals.project_count} iniciativas · Previsto total: {formatCurrency(data.totals.planned)} · Incurrido: {formatCurrency(data.totals.incurred)}
        </div>

        <div className="space-y-3" data-testid="cost-lines">
          {data.rows.map((row, i) => {
            const w = maxPlanned ? Math.max(4, (row.total_planned / maxPlanned) * 100) : 0;
            const wInc = row.total_planned ? Math.min(100, (row.total_incurred / row.total_planned) * 100) : 0;
            const isExpanded = expanded === row.business_line;
            return (
              <div key={row.business_line} data-testid={`cost-line-${i}`}>
                <button
                  type="button"
                  onClick={() => setExpanded(isExpanded ? null : row.business_line)}
                  className="w-full text-left"
                >
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-200 flex-1 truncate">{row.business_line}</span>
                    <Badge variant="outline" className="text-[9px]">{Math.round(row.project_count)} iniciativa(s)</Badge>
                    <span className="text-sm font-black text-slate-900 dark:text-white">{formatCurrency(row.total_planned)}</span>
                  </div>
                  <div className="relative h-5 bg-slate-100 dark:bg-slate-800 rounded overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-slate-300 dark:bg-slate-600 transition-all"
                         style={{ width: `${w}%` }} data-testid={`cost-bar-planned-${i}`} />
                    <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-600 to-red-500 transition-all"
                         style={{ width: `${(w * wInc) / 100}%` }} data-testid={`cost-bar-incurred-${i}`} />
                  </div>
                  <div className="flex items-baseline gap-3 text-[10px] text-muted-foreground mt-0.5">
                    <span className="text-red-700 dark:text-red-400">Incurrido: {formatCurrency(row.total_incurred)}{row.execution_pct != null && ` (${row.execution_pct}%)`}</span>
                    {row.total_external > 0 && <span className="text-amber-700 dark:text-amber-400">Externos: {formatCurrency(row.total_external)}{row.subsidy_pct != null && ` (${row.subsidy_pct}%)`}</span>}
                    <span className="ml-auto flex items-center gap-1"><TrendingUp className="w-3 h-3" />Media {formatCurrency(row.avg_cost_per_initiative)}/iniciativa</span>
                  </div>
                  {row.sub_groups && (
                    <div className="flex items-center gap-3 text-[10px] mt-1" data-testid={`cost-line-subgroups-${i}`}>
                      {Object.entries(row.sub_groups).map(([key, sg]) => (
                        <span key={key} className="px-1.5 py-0.5 rounded bg-sky-50 dark:bg-sky-950/30 text-sky-700 dark:text-sky-300">
                          {key}: {formatCurrency(sg.planned)} ({Math.round(sg.count)})
                        </span>
                      ))}
                    </div>
                  )}
                </button>

                {isExpanded && row.initiatives?.length > 0 && (
                  <div className="mt-2 ml-3 pl-3 border-l-2 border-emerald-500 space-y-1" data-testid={`cost-line-detail-${i}`}>
                    {row.initiatives.map((it, k) => (
                      <div key={it.id} className="flex items-center gap-2 text-xs py-0.5">
                        <span className="text-muted-foreground w-4">{k+1}</span>
                        <span className="flex-1 truncate">{it.name}</span>
                        {it.group && <Badge variant="outline" className="text-[9px] border-sky-400 text-sky-700">{it.group}</Badge>}
                        <Badge variant="outline" className="text-[9px]">{it.source}</Badge>
                        <span className="font-mono">{formatFullCurrency(it.planned)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
